import {
  AcademicCapIcon,
  BookOpenIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"
import H2 from "components/atoms/H2"
import ScrollAnimate from "components/atoms/common/ScrollAnimate"
import useTranslations from "hooks/useTranslations"
import { CardImages } from "./images"

const ICONS = [AcademicCapIcon, ShieldCheckIcon, BookOpenIcon]

// One product shot per card, in the same order as the cards: the teacher's
// lesson platform, the Get Help screen, and the feed of online safety bites.
const VISUALS = [CardImages.lessons, CardImages.help, CardImages.feed]

/**
 * "What we provide at a glance". The draft lays this out as a two-column
 * table (feature / what it means for you); on a marketing page that reads
 * better as three cards, which also stack cleanly on mobile.
 *
 * Solid mint cards with near-black text, the hero's own pairing. The section
 * itself stays white so the cards don't merge into the mint hero above.
 */
export default function ParentInfoFeatures() {
  const {
    parentInfoFeaturesTitle,
    parentInfoFeaturesNames,
    parentInfoFeaturesTexts,
  } = useTranslations()

  const names: string[] = parentInfoFeaturesNames || []
  const texts: string[] = parentInfoFeaturesTexts || []

  return (
    <div className="bg-white py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <H2
          size="lg"
          className="m-0 mb-8 whitespace-pre-line text-left font-extrabold tracking-tight"
          theme="light"
        >
          {parentInfoFeaturesTitle}
        </H2>
        <ul className="m-0 grid list-none grid-cols-1 gap-8 p-0 md:grid-cols-3">
          {names.map((name, index) => {
            const Icon = ICONS[index] || AcademicCapIcon
            const Visual = VISUALS[index]
            return (
              // 0.3s apart against ScrollAnimate's 0.5s entrance, so each card is
              // mostly settled before the next starts: read as one by one
              // rather than a ripple. h-full keeps the cards equal height.
              <ScrollAnimate key={name} delay={index * 0.3} className="h-full">
                <li className="h-full rounded-3xl bg-kkm-green p-6 lg:p-8">
                  <div className="relative mb-6 h-56 overflow-hidden rounded-2xl bg-white lg:h-64">
                    {Visual ? (
                      <Visual className="!absolute inset-0 !block h-full w-full !max-w-none" />
                    ) : null}
                  </div>
                  <h3 className="mb-4 flex items-center gap-4 font-display text-2xl font-bold leading-tight text-zinc-900">
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/30">
                      <Icon aria-hidden="true" className="h-10 w-10" />
                    </span>
                    {name}
                  </h3>
                  <p className="m-0 text-base leading-snug text-zinc-800">
                    {texts[index] || ""}
                  </p>
                </li>
              </ScrollAnimate>
            )
          })}
        </ul>
      </section>
    </div>
  )
}
